// The current-user READ surface — the hooks moved verbatim from
// `src/hooks/query/useQueryUser.ts` (now a delegate module re-exporting these)
// with the context-split, and the cache-scoped commands and projections that
// followed them with the hub tail.
//
// The fetch owner now lives beside them in `./useQueryUserForContext`; only the
// `CurrentUserProvider` MOUNT is still app-side, which is the query-as-prop
// inversion working as designed rather than residue.

import { logger } from '@heidi/runtime/logger';
import { LanguageCode } from '@heidi/schema/ml-scribe';

import { GET_USER } from '../../contracts/keys';
import type { UserResponse } from '../../contracts/types/user-types';
import { getWorkspaceDataConfig } from '../config';
import {
  type QueryUserQueryResult,
  type QueryUserResponse,
  type QueryUserSelector,
  useCurrentUserQueryResult,
} from '../current-user-context';

/**
 * Synchronous read of the cached `GET /user` response — moved from
 * `src/hooks/query/current-user-cache.ts` onto the seam's
 * queryClient, which is the app's `reactQueryClient` singleton, so the
 * instance is the same either way. Mirrors `getCurrentUserSnippets` /
 * `getCurrentUserPersonalizations`.
 *
 * NOT moved with it: `invalidateCurrentUser`. It is COMPOSITE over `GET_USER`
 * and COMMERCE's `GET_PRODUCTS`, and workspace is a foundational domain that
 * may not import a product domain — re-probed at this head, see the app-side
 * declaration for the gate output.
 */
export const getCurrentUser = () =>
  getWorkspaceDataConfig().queryClient.getQueryData<UserResponse>([GET_USER]);

/**
 * @deprecated Use `useUser` instead. Maintained for backwards compatibility as we migrate.
 */
export function useQueryUser<T = QueryUserResponse>(
  select?: QueryUserSelector<T>
): QueryUserQueryResult<T> {
  return useCurrentUserQueryResult(select);
}

export function useUser<T = QueryUserResponse>(
  select?: QueryUserSelector<T>
): QueryUserQueryResult<T> {
  return useCurrentUserQueryResult(select);
}

/**
 * Cancel any in-flight `GET /user/` requests.
 *
 * Call from a user-settings mutation's `onMutate`: a GET that started before
 * the PATCH (e.g. a window-focus refetch) can resolve after the PATCH's own
 * settled refetch and land pre-save values in the cache, reverting what the
 * user just saved everywhere the user query is read.
 *
 * The query's `queryFn` doesn't wire an abort signal, so this doesn't abort
 * the HTTP request itself — react-query just discards the response instead of
 * writing it to the cache, which is all the race fix needs.
 *
 * Runs against the seam's queryClient, which is the app's `reactQueryClient`
 * singleton its legacy `@/utils/reactQueryClient` import reached — the same
 * instance either way, exactly as `getCurrentUser` above.
 */
export const cancelCurrentUserQueries = async () => {
  try {
    await getWorkspaceDataConfig().queryClient.cancelQueries({ queryKey: [GET_USER] });
  } catch (error) {
    logger.error('Failed to cancel user queries:', error);
  }
};

/**
 * Whether the caller is looking at a team member's workspace rather than their
 * own — the COHORT read.
 *
 * `selectedReviewMemberAtom` is an APP atom, so its value crosses as a
 * parameter and the atom stays app-side; the delegate at
 * `src/hooks/query/useQueryUser.ts` reads it and keeps the no-argument
 * signature its callers use.
 *
 * NOT interchangeable with the session-by-id BRANCH read of the same name,
 * which additionally requires a loaded team id — `useSessionAccessContext`
 * carries both and its `isReviewingOtherUserCohort` member is this expression
 * inlined.
 */
export function useIsReviewingOtherUser(
  selectedReviewMemberId: string | null | undefined
): boolean {
  const { data: userData } = useQueryUser();
  if (!selectedReviewMemberId || !userData) {
    return false;
  }
  return selectedReviewMemberId !== userData?.kinde_user_id;
}

/**
 * The dictation flags the four projections below branch on, RESOLVED app-side.
 *
 * `isDictationLanguageSettingsEnabled` / `isDictationOutputLanguageEnabled` are
 * non-hook OpenFeature reads in `@/features/flags`; a package resolves no flag,
 * so the booleans cross and the keys stay app-side.
 *
 * ONE DISCLOSED DELTA, and it is a call count rather than a value. The legacy
 * bodies read each flag lazily behind a short-circuit, so
 * `getDefaultDictationOutputLanguages` called the language-settings getter
 * twice on the flag-on path and the output getter not at all on the flag-off
 * path. The delegate now resolves both once, up front. Both getters are pure
 * reads of the OpenFeature client with no exposure emit, so every returned
 * value is identical; only the number of reads changes.
 */
export interface DictationLanguageFlags {
  /** `isDictationLanguageSettingsEnabled()` */
  languageSettingsEnabled: boolean;
  /** `isDictationOutputLanguageEnabled()` */
  outputLanguageEnabled: boolean;
}

/** User's preferred dictation input languages. Legacy `session_settings`
 * cover users who haven't re-saved via the new settings UI. */
export const getDefaultDictationInputLanguages = (
  flags: Pick<DictationLanguageFlags, 'languageSettingsEnabled'>
): LanguageCode[] | undefined => {
  const user = getCurrentUser();
  const settings = user?.session_settings;

  if (flags.languageSettingsEnabled && user?.dictation?.input_languages?.length) {
    return user.dictation.input_languages;
  }

  if (settings?.default_input_language_code_list?.length) {
    return settings.default_input_language_code_list;
  }

  return settings?.default_input_language_code
    ? [settings.default_input_language_code]
    : undefined;
};

/** Output languages for the transcribe `user_context`. The override applies
 * only behind its own flag; otherwise output mirrors input. Exactly one of
 * `outputLanguages` (flag-on) / `outputLanguageCode` (legacy) is populated. */
export const getDefaultDictationOutputLanguages = (
  flags: DictationLanguageFlags
): {
  outputLanguages: LanguageCode[] | null;
  /** @deprecated Legacy single-language output; set on the flag-off path only. */
  outputLanguageCode: LanguageCode | null;
} => {
  const user = getCurrentUser();

  if (!flags.languageSettingsEnabled) {
    return {
      outputLanguages: null,
      outputLanguageCode:
        user?.session_settings?.default_output_language_code ?? LanguageCode.en,
    };
  }

  return {
    outputLanguages: (flags.outputLanguageEnabled &&
    user?.dictation?.output_languages?.length
      ? user?.dictation?.output_languages
      : getDefaultDictationInputLanguages(flags)) ?? [LanguageCode.en],
    outputLanguageCode: null,
  };
};

/**
 * Read the user's current `log_dictations_enabled` preference from the cached
 * user. Defaults to `true` to preserve today's behaviour for users on stale
 * caches or backends that haven't shipped the field yet.
 */
export const getLogDictationsEnabled = (): boolean => {
  const user = getCurrentUser();
  return user?.dictation?.log_dictations_enabled ?? true;
};

/**
 * Read the user's current `sound_enabled` preference (dictation start/stop/error
 * audio cues) from the cached user. Defaults to `true` to preserve today's
 * behaviour for users on stale caches or backends that haven't shipped the
 * field yet.
 */
export const getDictationSoundEnabled = (): boolean => {
  const user = getCurrentUser();
  return user?.dictation?.sound_enabled ?? true;
};
